<template>
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-4">
        <!-- The page heading was removed: it duplicated the "Postback Log" tab
             sitting right next to it, and on a phone it wrapped to two lines and
             squeezed the tab strip so "Test link" fell off the edge. -->
        <SelectButton
          v-model="view"
          :options="viewOptions"
          optionLabel="label"
          optionValue="value"
        />
      </div>
      <!-- Setup checker and Test link take a link / click id as their input and
           never receive :dateRange, so the picker did nothing on those tabs —
           and on a phone it still ate width next to the tab strip. -->
      <div v-if="usesDateRange" class="flex items-center gap-2">
        <DatePicker
          v-model="dateRange"
          selectionMode="range"
          showIcon
          :showOnFocus="true"
          class="w-auto"
        />
        <Button
          v-if="dateRange && dateRange.length === 2"
          icon="pi pi-times"
          severity="secondary"
          outlined
          @click="clearDateRange"
          v-tooltip="'Clear date range'"
        />
      </div>
    </div>

    <PostBackLog v-if="view === 'log'" :dateRange="dateRange" />
    <ParamsBreakdown v-else-if="view === 'params'" :dateRange="dateRange" />
    <PostbackChecker v-else-if="view === 'checker'" />
    <TestLink v-else-if="view === 'testlink'" />
    <DepositsByClickId v-else :dateRange="dateRange" />
  </template>

  <script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import DatePicker from 'primevue/datepicker';
  import SelectButton from 'primevue/selectbutton';
  import Button from 'primevue/button';
  import PostBackLog from './PostbackLog.vue';
  import DepositsByClickId from './DepositsByClickId.vue';
  import ParamsBreakdown from '../ParamsBreakdown.vue';
  import PostbackChecker from './PostbackChecker.vue';
  import TestLink from './TestLink.vue';
  import PostbackService from '@/services/PostbackService';

  const route = useRoute();
  const router = useRouter();

  // Set default to last 2 days including today
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const dateRange = ref([yesterday, today]);

  // Conditional visibility for the Params breakdown tab. Kept hidden by default
  // so nothing changes for scopes that never see hyphenated campaignIds. Flips
  // true once the has-params check comes back positive.
  const hasParams = ref(false);
  onMounted(async () => {
    try {
      const res = await PostbackService.hasParamsPostbacks();
      hasParams.value = !!res.data?.has;
    } catch (err) {
      console.error('Error probing params availability:', err);
      hasParams.value = false;
    }
    // If the URL asked for the params view but the scope has none, drop back
    // to the log view silently — avoids showing a broken/empty page for
    // stale bookmarks or URL tampering.
    if (view.value === 'params' && !hasParams.value) {
      view.value = 'log';
      router.replace({ query: { ...route.query, view: undefined } });
    }
  });

  const view = ref(route.query.view === 'deposits' ? 'deposits'
                : route.query.view === 'params'   ? 'params'
                : route.query.view === 'checker'  ? 'checker'
                : route.query.view === 'testlink' ? 'testlink'
                : 'log');
  // Which tabs actually read the date range. Keep this in step with the
  // component list below: anything rendered WITHOUT :dateRange belongs here.
  const DATELESS_VIEWS = ['checker', 'testlink'];
  const usesDateRange = computed(() => !DATELESS_VIEWS.includes(view.value));

  const viewOptions = computed(() => {
    const opts = [
      { label: 'Postback Log', value: 'log' },
      { label: 'Deposits by Click ID', value: 'deposits' }
    ];
    if (hasParams.value) opts.push({ label: 'Params breakdown', value: 'params' });
    // Always available — it's a config check, not a data view, so it's useful
    // precisely when there is no postback traffic to look at.
    opts.push({ label: 'Setup checker', value: 'checker' });
    opts.push({ label: 'Test link', value: 'testlink' });
    return opts;
  });

  // Sync view changes to URL
  watch(view, (newView) => {
    const query = { ...route.query };
    if (newView === 'deposits') query.view = 'deposits';
    else if (newView === 'params') query.view = 'params';
    else if (newView === 'checker') query.view = 'checker';
    else if (newView === 'testlink') query.view = 'testlink';
    else delete query.view;
    router.replace({ query });

    // When switching to deposits view, expand date range to at least last 30 days
    if (newView === 'deposits') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      if (!dateRange.value || !dateRange.value[0] || dateRange.value[0] > thirtyDaysAgo) {
        dateRange.value = [thirtyDaysAgo, new Date()];
      }
    }
  }, { immediate: true });

  const clearDateRange = () => {
    dateRange.value = null;
  };
  </script>
